/**
   * Predictive search custom element.
   * Listens for input on the search field, shows a skeleton loader while
   * fetching results via StorefrontAPI.predictiveSearch(), and renders
   * product results or an empty state in the dropdown.
   */
  class PredictiveSearchElement extends HTMLElement {
    constructor() {
      super();
      this._debounceTimer = null;
      this._abortController = null;
      this._isOpen = false;
    }

    connectedCallback() {
      this._input = this.querySelector('input[type="search"]');
      this._resultsContainer = this.querySelector('#predictive-search-results');
      this._field = this.querySelector('.header__search-field');
      if (!this._input || !this._resultsContainer) return;

      // Teleport the dropdown to <body> so no header/menu stacking context can
      // trap or clip it; we position it under the search bar ourselves.
      document.body.appendChild(this._resultsContainer);
      this._boundReposition = this._reposition.bind(this);

      this._boundClickOutside = this._onClickOutside.bind(this);
      this._input.addEventListener('input', this._onInput.bind(this));
      this._input.addEventListener('focus', this._onFocus.bind(this));
      document.addEventListener('click', this._boundClickOutside);
      this._input.addEventListener('keydown', this._onKeydown.bind(this));

      // Visual search: camera icon → pick/take a photo → vision → search.
      this._cameraBtn = this.querySelector('.header__camera-btn');
      this._fileInput = this.querySelector('.header__camera-input');
      if (this._cameraBtn) this._cameraBtn.addEventListener('click', this._onCameraClick.bind(this));
      if (this._fileInput) this._fileInput.addEventListener('change', this._onImageSelected.bind(this));
    }

    disconnectedCallback() {
      clearTimeout(this._debounceTimer);
      if (this._boundClickOutside) document.removeEventListener('click', this._boundClickOutside);
      if (this._boundReposition) {
        window.removeEventListener('scroll', this._boundReposition);
        window.removeEventListener('resize', this._boundReposition);
      }
    }

    /** Anchor the (teleported) dropdown directly under the search field. */
    _reposition() {
      const anchor = this._field || this._input;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const s = this._resultsContainer.style;
      s.top = (rect.bottom + 8) + 'px';
      s.left = rect.left + 'px';
      s.width = rect.width + 'px';
    }

    _onInput() {
      clearTimeout(this._debounceTimer);
      const query = this._input.value.trim();

      if (query.length < 2) {
        this._close();
        return;
      }

      this._showLoading();
      this._debounceTimer = setTimeout(() => this._fetchResults(query), 300);
    }

    _onFocus() {
      const query = this._input.value.trim();
      if (query.length >= 2 && this._resultsContainer.innerHTML.trim()) {
        this._open();
      }
    }

    _onKeydown(e) {
      if (e.key === 'Escape') {
        this._close();
        this._input.blur();
      }
    }

    // ── Visual search ──────────────────────────────────────────
    _onCameraClick() {
      if (this._fileInput) this._fileInput.click();
    }

    async _onImageSelected(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = ''; // allow re-picking the same file later
      if (!file || !/^image\//.test(file.type)) return;

      this._input.value = '';
      this._showLoading();
      try {
        const dataUrl = await this._resizeImage(file, 512, 0.8);
        const res = await fetch('/apps/surprav/visual-search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ image: dataUrl })
        });
        if (!res.ok) { this._renderError(); return; }
        const data = await res.json();
        if (data && Array.isArray(data.products) && data.products.length) {
          // True visual matches — render them directly. No query term, no fallback.
          this._renderResults({ products: data.products, collections: [] }, '');
        } else {
          // Honest: we found nothing visually close in the collection.
          this._renderVisualNotFound();
        }
      } catch (_err) {
        this._renderError();
      }
    }

    /** Downscale + re-encode the chosen photo so the upload stays small/fast. */
    _resizeImage(file, maxSize, quality) {
      return new Promise(function (resolve, reject) {
        var img = new Image();
        var url = URL.createObjectURL(file);
        img.onload = function () {
          URL.revokeObjectURL(url);
          var w = img.naturalWidth || img.width;
          var h = img.naturalHeight || img.height;
          if (w >= h && w > maxSize) { h = Math.round(h * maxSize / w); w = maxSize; }
          else if (h > maxSize) { w = Math.round(w * maxSize / h); h = maxSize; }
          var canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          try { resolve(canvas.toDataURL('image/jpeg', quality)); }
          catch (err) { reject(err); }
        };
        img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('image_load_failed')); };
        img.src = url;
      });
    }

    _onClickOutside(e) {
      if (!this.contains(e.target) && !this._resultsContainer.contains(e.target)) {
        this._close();
      }
    }

    _open() {
      this._reposition();
      this._resultsContainer.classList.add('search-results', 'is-active');
      this._input.setAttribute('aria-expanded', 'true');
      window.addEventListener('scroll', this._boundReposition, { passive: true });
      window.addEventListener('resize', this._boundReposition);
      this._isOpen = true;
    }

    _close() {
      this._resultsContainer.classList.remove('is-active');
      this._input.setAttribute('aria-expanded', 'false');
      window.removeEventListener('scroll', this._boundReposition);
      window.removeEventListener('resize', this._boundReposition);
      this._isOpen = false;
    }

    _showLoading() {
      this._resultsContainer.innerHTML =
        '<div class="search-results__loading">' +
          '<div class="skeleton-loader skeleton-loader--text" aria-hidden="true">' +
            '<div class="skeleton-loader__text-block">' +
              '<div class="skeleton-loader__shimmer skeleton-loader__line skeleton-loader__line--wide"></div>' +
              '<div class="skeleton-loader__shimmer skeleton-loader__line skeleton-loader__line--medium"></div>' +
              '<div class="skeleton-loader__shimmer skeleton-loader__line skeleton-loader__line--narrow"></div>' +
            '</div>' +
            '<div class="skeleton-loader__text-block">' +
              '<div class="skeleton-loader__shimmer skeleton-loader__line skeleton-loader__line--wide"></div>' +
              '<div class="skeleton-loader__shimmer skeleton-loader__line skeleton-loader__line--medium"></div>' +
            '</div>' +
          '</div>' +
          '<span class="visually-hidden">Laden…</span>' +
        '</div>';
      this._open();
    }

    async _fetchResults(query) {
      if (this._abortController) {
        this._abortController.abort();
      }
      this._abortController = new AbortController();

      try {
        const api = window.SurpravAPI;
        if (!api || typeof api.predictiveSearch !== 'function') {
          this._renderError();
          return;
        }

        const data = await api.predictiveSearch(query);

        // Check if input changed while we were fetching
        if (this._input.value.trim() !== query) return;

        if (!data || (!data.products?.length && !data.collections?.length)) {
          this._renderEmpty(query);
        } else {
          this._renderResults(data, query);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          this._renderError();
        }
      }
    }

    _renderResults(data, query) {
      const products = data.products || [];
      const collections = data.collections || [];
      const count = products.length + collections.length;

      let html = '<div class="search-results__header">' +
        '<span class="search-results__count">' + count + ' resultaten</span>' +
        '</div>';

      // Categories first (quick jump), then products.
      if (collections.length) {
        html += '<div class="search-results__section-label">Categorieën</div>' +
          '<div class="search-results__list" role="listbox">';
        for (const col of collections) {
          const colHandle = encodeURIComponent(col.handle || '');
          const colImg = col.image && col.image.url ? this._escapeHtml(col.image.url) : '';
          html += '<a href="/collections/' + colHandle + '" class="search-result-item search-result-item--collection" role="option">' +
            '<div class="search-result-item__image search-result-item__image--collection">' +
              (colImg
                ? '<img src="' + colImg + '" alt="" loading="lazy" width="44" height="44">'
                : '<span class="search-result-item__cat-icon" aria-hidden="true">&#9638;</span>') +
            '</div>' +
            '<div class="search-result-item__details">' +
              '<p class="search-result-item__title">' + this._highlightQuery(col.title, query) + '</p>' +
              '<span class="search-result-item__meta">Categorie</span>' +
            '</div>' +
          '</a>';
        }
        html += '</div>';
      }

      if (products.length) {
        html += '<div class="search-results__section-label">Producten</div>';
      }
      html += '<div class="search-results__list" role="listbox">';

      for (const product of products) {
        const price = product.priceRange?.minVariantPrice;
        const formattedPrice = price
          ? new Intl.NumberFormat('nl-NL', { style: 'currency', currency: price.currencyCode }).format(price.amount)
          : '';
        const imgUrl = product.featuredImage?.url || '';
        const imgAlt = product.featuredImage?.altText || product.title;
        const safeHandle = encodeURIComponent(product.handle || '');
        const safeImgUrl = this._escapeHtml(imgUrl);
        const safeAlt = this._escapeHtml(imgAlt);

        html += '<a href="/products/' + safeHandle + '" class="search-result-item" role="option">' +
          '<div class="search-result-item__image">' +
            (imgUrl
              ? '<img src="' + safeImgUrl + '" alt="' + safeAlt + '" loading="lazy" width="60" height="60">'
              : '<div class="search-result-item__placeholder"></div>') +
          '</div>' +
          '<div class="search-result-item__details">' +
            '<p class="search-result-item__title">' + this._highlightQuery(product.title, query) + '</p>' +
            '<div class="search-result-item__price">' +
              '<span class="search-result-item__current-price">' + formattedPrice + '</span>' +
            '</div>' +
          '</div>' +
        '</a>';
      }

      html += '</div>';
      // Only show "view all results" for a TEXT query — a visual search has no
      // search term, so the link would land on an empty /search page.
      if (query) {
        html += '<div class="search-results__footer">' +
          '<a href="/search?q=' + encodeURIComponent(query) + '" class="search-results__view-all">Alle resultaten bekijken</a>' +
          '</div>';
      }

      this._resultsContainer.innerHTML = html;
      this._open();
    }

    _renderEmpty(query) {
      const suggestions = ['Trending', 'Bestsellers', 'Nieuw binnen', 'Accessoires'];
      let suggestionsHtml = '';
      for (const s of suggestions) {
        suggestionsHtml += '<a href="/search?q=' + encodeURIComponent(s) + '" class="search-results__suggestion">' + s + '</a> ';
      }

      this._resultsContainer.innerHTML =
        '<div class="search-results__empty">' +
          '<p>Geen resultaten gevonden voor "' + this._escapeHtml(query) + '"</p>' +
          '<p class="search-results__suggestion">Populaire zoekopdrachten: ' + suggestionsHtml + '</p>' +
        '</div>';
      this._open();
    }

    _renderError() {
      this._resultsContainer.innerHTML =
        '<div class="search-results__error">' +
          '<p>Er ging iets mis. Probeer het opnieuw.</p>' +
        '</div>';
      this._open();
    }

    _renderVisualNotFound() {
      this._resultsContainer.innerHTML =
        '<div class="search-results__empty">' +
          '<p>Dit hebben we (nog) niet in onze collectie.</p>' +
          '<p class="search-results__suggestion">' +
            'We vonden geen product dat hier visueel op lijkt. Probeer een andere foto of zoek op trefwoord.' +
          '</p>' +
        '</div>';
      this._open();
    }

    _escapeHtml(str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    _highlightQuery(text, query) {
      const safe = this._escapeHtml(text);
      if (!query) return safe;
      const q = this._escapeHtml(String(query)).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return safe.replace(new RegExp('(' + q + ')', 'gi'), '<mark>$1</mark>');
    }
  }

  if (!customElements.get('surprav-predictive-search')) {
    customElements.define('surprav-predictive-search', PredictiveSearchElement);
  }
